import { computed, Injectable, signal } from '@angular/core';
import type { User, CountryDetail } from '../models';

/**
 * AuthStore — in-memory session state.
 *
 * Owns *identity*: who is signed in, against which merchant, with which tokens. It does
 * **not** own the account list — that belongs to `AccountStore` (decision D3). Holding two
 * independent copies meant the picker and the switcher could disagree about what existed.
 *
 * Security:
 * - Access token in memory only, never persisted.
 * - Refresh token in localStorage (survives a page refresh) via `Storage`.
 * - Session cleared on logout or refresh failure.
 * - Client-side expiry tracking with a 30 s safety buffer.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthStore {
  // Private signals for internal state
  private readonly accessToken = signal<string | null>(null);
  private readonly tokenExpiry = signal<number | null>(null);
  private readonly user = signal<User | null>(null);
  private readonly countryDetail = signal<CountryDetail | null>(null);
  private readonly token = signal<string | null>(null);
  private readonly merchantId = signal<string | null>(null);
  private readonly merchantUserId = signal<string | null>(null);

  // Public computed signals
  readonly currentUser = computed(() => this.user());
  readonly currentCountryDetail = computed(() => this.countryDetail());
  readonly isAuthenticated = computed(() => !!this.accessToken() && !!this.user());

  /**
   * Install a session.
   *
   * `merchantId` must be the merchant the session is actually scoped to — for a user with
   * several businesses that is whichever one they chose, not their primary. Writing a stale
   * value here is what made requests go out with mixed identity.
   */
  setSession(config: {
    accessToken: string;
    expiresInSeconds: number;
    user: User;
    countryDetail: CountryDetail;
    token: string;
    merchantId: string;
    merchantUserId: string;
  }): void {
    this.accessToken.set(config.accessToken);
    this.tokenExpiry.set(Date.now() + config.expiresInSeconds * 1000);
    this.user.set(config.user);
    this.countryDetail.set(config.countryDetail);
    this.token.set(config.token);
    this.merchantId.set(config.merchantId);
    this.merchantUserId.set(config.merchantUserId);
  }

  /**
   * Update access token and expiration (used during token refresh)
   */
  updateAccessToken(accessToken: string, expiresInSeconds: number): void {
    this.accessToken.set(accessToken);
    this.tokenExpiry.set(Date.now() + expiresInSeconds * 1000);
  }

  /**
   * Clear authentication session
   */
  clearSession(): void {
    this.accessToken.set(null);
    this.tokenExpiry.set(null);
    this.user.set(null);
    this.countryDetail.set(null);
    this.token.set(null);
    this.merchantId.set(null);
    this.merchantUserId.set(null);
  }

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    return this.accessToken();
  }

  /**
   * Check if access token is expired
   */
  isTokenExpired(): boolean {
    const expiry = this.tokenExpiry();
    if (!expiry) {
      return true;
    }
    // Add 30 second buffer to refresh before actual expiration
    return Date.now() >= expiry - 30000;
  }

  /**
   * Check if user has a valid session
   */
  hasValidSession(): boolean {
    return this.isAuthenticated() && !this.isTokenExpired();
  }

  /**
   * Get the opaque legacy token (distinct from the JWT access token)
   */
  getToken(): string | null {
    return this.token();
  }

  /**
   * Get the merchant the current session is scoped to
   */
  getMerchantId(): string | null {
    return this.merchantId();
  }

  /**
   * Get current merchant user ID
   */
  getMerchantUserId(): string | null {
    return this.merchantUserId();
  }
}
